// AGC: CouponAdmin - Admin panel for managing coupon/promo codes
// AGC: CRUD operations via backend API at /api/v1/coupons

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { getBackendUrl } from '../../utils/backendUrl'
import {
  Ticket,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertTriangle,
  Check,
  X,
} from 'lucide-react'

// AGC: Coupon data interface matching backend schema
interface Coupon {
  code: string
  type: 'tier_upgrade' | 'discount_percentage' | 'discount_fixed' | 'free_trial'
  targetTier: string | null
  discountPercent: number | null
  discountFixed: number | null
  durationMonths: number
  fromTiers: string[]
  toTiers: string[]
  maxUses: number | null
  currentUses: number
  maxUsesPerUser: number
  validFrom: string
  validUntil: string | null
  isActive: boolean
  description: string
  // AGC: allowExtend — when true, users who already have targetTier get extra months
  allowExtend: boolean
  // AGC: Sorteio (raffle) fields — optional, only present when hasSorteio is true
  hasSorteio: boolean
  sorteioPrize: string
  sorteioPrizeDescription: string
  sorteioPrizeImageUrl: string
  sorteioPrizeValue: string
  sorteioDrawDate: string
}

// AGC: Empty coupon template for create form
const EMPTY_COUPON: Coupon = {
  code: '',
  type: 'tier_upgrade',
  targetTier: null,
  discountPercent: null,
  discountFixed: null,
  durationMonths: 1,
  fromTiers: [],
  toTiers: [],
  maxUses: null,
  currentUses: 0,
  maxUsesPerUser: 1,
  validFrom: new Date().toISOString().split('T')[0],
  validUntil: null,
  isActive: true,
  description: '',
  allowExtend: false,
  // AGC: Sorteio fields default to disabled/empty
  hasSorteio: false,
  sorteioPrize: '',
  sorteioPrizeDescription: '',
  sorteioPrizeImageUrl: '',
  sorteioPrizeValue: '',
  sorteioDrawDate: '',
}

// AGC: Coupon type options for select
const COUPON_TYPES: Coupon['type'][] = [
  'tier_upgrade',
  'discount_percentage',
  'discount_fixed',
  'free_trial',
]

// AGC: Available tiers for checkboxes/selects
const TIERS = ['free', 'pro', 'premium']

export const CouponAdmin: React.FC = () => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()

  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [unlimitedUses, setUnlimitedUses] = useState(true)

  // AGC: Fetch all coupons from backend
  const fetchCoupons = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAuthToken()
      const res = await fetch(`${getBackendUrl()}/api/v1/coupons`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = await res.json()
      // AGC: Ensure we always set an array
      const list = Array.isArray(data.coupons) ? data.coupons : Array.isArray(data) ? data : []
      setCoupons(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [getAuthToken])

  useEffect(() => {
    fetchCoupons()
  }, [fetchCoupons])

  // AGC: Helper to get auth headers
  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }, [getAuthToken])

  // AGC: Open create form
  const handleCreate = () => {
    setEditingCoupon({ ...EMPTY_COUPON })
    setIsCreating(true)
    setUnlimitedUses(true)
  }

  // AGC: Open edit form
  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon({ ...coupon })
    setIsCreating(false)
    setUnlimitedUses(coupon.maxUses === null)
  }

  // AGC: Cancel form
  const handleCancel = () => {
    setEditingCoupon(null)
    setIsCreating(false)
    setDeleteConfirm(null)
  }

  // AGC: Save coupon (create or update)
  const handleSave = async () => {
    if (!editingCoupon) return
    setSaving(true)
    setError(null)
    try {
      const headers = await authHeaders()
      // AGC: Convert sorteioPrizeValue string to number for backend; omit if empty
      const payload = {
        ...editingCoupon,
        code: editingCoupon.code.toUpperCase(),
        maxUses: unlimitedUses ? null : editingCoupon.maxUses,
        sorteioPrizeValue: editingCoupon.sorteioPrizeValue
          ? Number(editingCoupon.sorteioPrizeValue)
          : undefined,
      }

      if (isCreating) {
        const res = await fetch(`${getBackendUrl()}/api/v1/coupons`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        })
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      } else {
        const res = await fetch(
          `${getBackendUrl()}/api/v1/coupons/${encodeURIComponent(editingCoupon.code)}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(payload),
          }
        )
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      }

      setEditingCoupon(null)
      setIsCreating(false)
      await fetchCoupons()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  // AGC: Toggle coupon active/inactive
  const handleToggle = async (code: string) => {
    setError(null)
    try {
      const headers = await authHeaders()
      const res = await fetch(
        `${getBackendUrl()}/api/v1/coupons/${encodeURIComponent(code)}/toggle`,
        { method: 'PATCH', headers }
      )
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      await fetchCoupons()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  // AGC: Delete coupon after confirmation
  const handleDelete = async (code: string) => {
    setError(null)
    try {
      const headers = await authHeaders()
      const res = await fetch(
        `${getBackendUrl()}/api/v1/coupons/${encodeURIComponent(code)}`,
        { method: 'DELETE', headers }
      )
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      setDeleteConfirm(null)
      await fetchCoupons()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  // AGC: Update a field on the editing coupon
  const updateField = <K extends keyof Coupon>(key: K, value: Coupon[K]) => {
    if (!editingCoupon) return
    setEditingCoupon({ ...editingCoupon, [key]: value })
  }

  // AGC: Toggle a tier in fromTiers array
  const toggleFromTier = (tier: string) => {
    if (!editingCoupon) return
    const current = editingCoupon.fromTiers
    const updated = current.includes(tier)
      ? current.filter((t) => t !== tier)
      : [...current, tier]
    updateField('fromTiers', updated)
  }

  // AGC: Check if type needs targetTier field
  const needsTargetTier = (type: Coupon['type']) =>
    type === 'tier_upgrade' || type === 'free_trial'

  // AGC: Check if type needs discount percent
  const needsDiscountPercent = (type: Coupon['type']) =>
    type === 'discount_percentage'

  // AGC: Check if type needs discount fixed
  const needsDiscountFixed = (type: Coupon['type']) =>
    type === 'discount_fixed'

  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Ticket className="w-6 h-6 text-green-600 dark:text-green-400" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {t('admin.coupons.title')}
          </h2>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-brand-primary dark:bg-brand-light hover:bg-brand-dark dark:hover:opacity-90 text-white rounded-lg shadow-card dark:shadow-none transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('admin.coupons.create')}
        </button>
      </div>

      {/* AGC: Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* AGC: Create/Edit form card */}
      {editingCoupon && (
        <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-lg border border-slate-200 dark:border-white/5 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isCreating
              ? t('admin.coupons.createTitle')
              : t('admin.coupons.editTitle')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* AGC: Code field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.coupons.code')}
              </label>
              <input
                type="text"
                value={editingCoupon.code}
                onChange={(e) => updateField('code', e.target.value.toUpperCase())}
                disabled={!isCreating}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="PROMO2024"
              />
            </div>

            {/* AGC: Type select */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.coupons.type')}
              </label>
              <select
                value={editingCoupon.type}
                onChange={(e) => updateField('type', e.target.value as Coupon['type'])}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {COUPON_TYPES.map((ct) => (
                  <option key={ct} value={ct}>
                    {t(`admin.coupons.types.${ct}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* AGC: Conditional targetTier */}
            {needsTargetTier(editingCoupon.type) && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('admin.coupons.targetTier')}
                </label>
                <select
                  value={editingCoupon.targetTier ?? ''}
                  onChange={(e) =>
                    updateField('targetTier', e.target.value || null)
                  }
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">{t('admin.coupons.selectTier')}</option>
                  <option value="pro">Pro</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            )}

            {/* AGC: Conditional discountPercent */}
            {needsDiscountPercent(editingCoupon.type) && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('admin.coupons.discountPercent')}
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={editingCoupon.discountPercent ?? 0}
                  onChange={(e) =>
                    updateField('discountPercent', Number(e.target.value))
                  }
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )}

            {/* AGC: Conditional discountFixed */}
            {needsDiscountFixed(editingCoupon.type) && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('admin.coupons.discountFixed')}
                </label>
                <input
                  type="number"
                  min={0}
                  value={editingCoupon.discountFixed ?? 0}
                  onChange={(e) =>
                    updateField('discountFixed', Number(e.target.value))
                  }
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )}

            {/* AGC: Duration months */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.coupons.durationMonths')}
              </label>
              <input
                type="number"
                min={1}
                value={editingCoupon.durationMonths}
                onChange={(e) =>
                  updateField('durationMonths', Number(e.target.value))
                }
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* AGC: Max uses with unlimited toggle */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.coupons.maxUses')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  value={editingCoupon.maxUses ?? ''}
                  onChange={(e) =>
                    updateField('maxUses', e.target.value ? Number(e.target.value) : null)
                  }
                  disabled={unlimitedUses}
                  className="flex-1 px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 disabled:opacity-50 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <label className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={unlimitedUses}
                    onChange={(e) => {
                      setUnlimitedUses(e.target.checked)
                      if (e.target.checked) updateField('maxUses', null)
                    }}
                    className="rounded border-slate-200 dark:border-white/10 text-green-600 focus:ring-green-500"
                  />
                  {t('admin.coupons.unlimited')}
                </label>
              </div>
            </div>

            {/* AGC: Max uses per user */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.coupons.maxUsesPerUser')}
              </label>
              <input
                type="number"
                min={1}
                value={editingCoupon.maxUsesPerUser}
                onChange={(e) =>
                  updateField('maxUsesPerUser', Number(e.target.value))
                }
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* AGC: Valid from */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.coupons.validFrom')}
              </label>
              <input
                type="date"
                value={editingCoupon.validFrom}
                onChange={(e) => updateField('validFrom', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* AGC: Valid until */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('admin.coupons.validUntil')}
              </label>
              <input
                type="date"
                value={editingCoupon.validUntil ?? ''}
                onChange={(e) =>
                  updateField('validUntil', e.target.value || null)
                }
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* AGC: From tiers checkboxes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {t('admin.coupons.fromTiers')}
            </label>
            <div className="flex gap-4">
              {TIERS.map((tier) => (
                <label
                  key={tier}
                  className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"
                >
                  <input
                    type="checkbox"
                    checked={editingCoupon.fromTiers.includes(tier)}
                    onChange={() => toggleFromTier(tier)}
                    className="rounded border-slate-200 dark:border-white/10 text-green-600 focus:ring-green-500"
                  />
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </label>
              ))}
            </div>
          </div>

          {/* AGC: Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('admin.coupons.description')}
            </label>
            <input
              type="text"
              value={editingCoupon.description}
              onChange={(e) => updateField('description', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* AGC: allowExtend toggle — lets premium users extend subscription */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={editingCoupon.allowExtend}
                onChange={(e) => updateField('allowExtend', e.target.checked)}
                className="rounded border-slate-200 dark:border-white/10 text-blue-600 focus:ring-blue-500 w-4 h-4"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('admin.coupons.allowExtend', 'Permitir extensão (premium pode ganhar meses extras)')}
              </span>
            </label>
          </div>

          {/* AGC: Sorteio toggle — enables raffle entry for this coupon */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={editingCoupon.hasSorteio}
                onChange={(e) => updateField('hasSorteio', e.target.checked)}
                className="rounded border-slate-200 dark:border-white/10 text-purple-600 focus:ring-purple-500 w-4 h-4"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('admin.coupons.sorteio.enable')}
              </span>
            </label>

            {/* AGC: Expanded sorteio fields — only shown when hasSorteio is checked */}
            {editingCoupon.hasSorteio && (
              <div className="bg-slate-100 dark:bg-white/[0.04] rounded-xl p-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.coupons.sorteio.prize')}
                  </label>
                  <input
                    type="text"
                    value={editingCoupon.sorteioPrize}
                    onChange={(e) => updateField('sorteioPrize', e.target.value)}
                    placeholder="iPhone 16 Pro"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.coupons.sorteio.prizeDescription')}
                  </label>
                  <input
                    type="text"
                    value={editingCoupon.sorteioPrizeDescription}
                    onChange={(e) => updateField('sorteioPrizeDescription', e.target.value)}
                    placeholder={t('admin.coupons.sorteio.prizeDescriptionPlaceholder')}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* AGC: Prize image URL */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.coupons.sorteio.prizeImage')}
                  </label>
                  <input
                    type="url"
                    value={editingCoupon.sorteioPrizeImageUrl}
                    onChange={(e) => updateField('sorteioPrizeImageUrl', e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {editingCoupon.sorteioPrizeImageUrl && (
                    <img src={editingCoupon.sorteioPrizeImageUrl} alt="Prize" className="mt-2 h-24 rounded-lg object-cover" />
                  )}
                </div>

                {/* AGC: Prize value */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.coupons.sorteio.prizeValue')}
                  </label>
                  <input
                    type="number"
                    value={editingCoupon.sorteioPrizeValue}
                    onChange={(e) => updateField('sorteioPrizeValue', e.target.value)}
                    placeholder="8999"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('admin.coupons.sorteio.drawDate')}
                  </label>
                  <input
                    type="date"
                    value={editingCoupon.sorteioDrawDate}
                    onChange={(e) => updateField('sorteioDrawDate', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          {/* AGC: Active toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateField('isActive', !editingCoupon.isActive)}
              className="text-slate-600 dark:text-slate-300"
            >
              {editingCoupon.isActive ? (
                <ToggleRight className="w-8 h-8 text-green-600 dark:text-green-400" />
              ) : (
                <ToggleLeft className="w-8 h-8 text-slate-400 dark:text-slate-500" />
              )}
            </button>
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {editingCoupon.isActive
                ? t('admin.coupons.active')
                : t('admin.coupons.inactive')}
            </span>
          </div>

          {/* AGC: Save / Cancel buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !editingCoupon.code}
              className="flex items-center gap-2 px-4 py-2 bg-brand-primary dark:bg-brand-light hover:bg-brand-dark dark:hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg shadow-card dark:shadow-none transition-colors"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {t('admin.coupons.save')}
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 px-4 py-2 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.15] text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              {t('admin.coupons.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* AGC: Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="bg-error-50 dark:bg-red-500/15 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-error-700 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm">
              {t('admin.coupons.deleteConfirm', { code: deleteConfirm })}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleDelete(deleteConfirm)}
              className="px-3 py-1.5 bg-error-600 dark:bg-error-500 hover:bg-error-700 dark:hover:bg-error-600 text-white text-sm rounded-lg transition-colors"
            >
              {t('admin.coupons.confirmDelete')}
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-3 py-1.5 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.15] text-slate-700 dark:text-slate-300 text-sm rounded-lg transition-colors"
            >
              {t('admin.coupons.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* AGC: Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      )}

      {/* AGC: Coupons table */}
      {!loading && coupons.length === 0 && (
        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
          <Ticket className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{t('admin.coupons.empty')}</p>
        </div>
      )}

      {!loading && coupons.length > 0 && (
        <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-lg border border-slate-200 dark:border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-white/[0.02]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    {t('admin.coupons.code')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    {t('admin.coupons.type')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    {t('admin.coupons.targetTier')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    {t('admin.coupons.uses')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    {t('admin.coupons.status')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    {t('admin.coupons.validDates')}
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600 dark:text-slate-300">
                    {t('admin.coupons.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {coupons.map((coupon) => (
                  <tr
                    key={coupon.code}
                    className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900 dark:text-slate-100">
                      {coupon.code}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {t(`admin.coupons.types.${coupon.type}`)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {coupon.targetTier ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {coupon.currentUses}/{coupon.maxUses ?? '\u221E'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          coupon.isActive
                            ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                            : 'bg-slate-100 dark:bg-white/[0.04] text-slate-500 dark:text-slate-400'
                        }`}
                      >
                        {coupon.isActive
                          ? t('admin.coupons.active')
                          : t('admin.coupons.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">
                      {coupon.validFrom}
                      {coupon.validUntil ? ` — ${coupon.validUntil}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title={t('admin.coupons.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggle(coupon.code)}
                          className="p-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors"
                          title={t('admin.coupons.toggle')}
                        >
                          {coupon.isActive ? (
                            <ToggleRight className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(coupon.code)}
                          className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title={t('admin.coupons.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
